/**
 * Instagram Graph API Integration
 *
 * Setup Instructions:
 * 1. Create a Facebook Developer account at https://developers.facebook.com/
 * 2. Create a new app and add Instagram Graph API
 * 3. Get your Instagram Business Account ID
 * 4. Generate an access token with instagram_basic permission
 * 5. Add credentials to .env file:
 *    - REACT_APP_INSTAGRAM_ACCESS_TOKEN=your_access_token
 *    - REACT_APP_INSTAGRAM_USER_ID=your_instagram_business_account_id
 */

// Use Facebook Graph API for Business/Creator accounts (tokens starting with EAAc)
// Use Instagram Graph API for Basic Display API (tokens starting with IGAA)
const FACEBOOK_API_BASE = 'https://graph.facebook.com/v18.0';
const INSTAGRAM_API_BASE = 'https://graph.instagram.com';

/**
 * Fetch Instagram posts from the user's feed
 * @param {number} limit - Number of posts to fetch (default: 12)
 * @returns {Promise<Array>} Array of Instagram post objects
 */
export const fetchInstagramPosts = async (limit = 12) => {
  const accessToken = process.env.REACT_APP_INSTAGRAM_ACCESS_TOKEN;
  const userId = process.env.REACT_APP_INSTAGRAM_USER_ID;

  // If no credentials, return mock data
  if (!accessToken || !userId) {
    console.warn('Instagram API credentials not found. Using mock data.');
    return getMockInstagramPosts();
  }

  try {
    // Determine which API to use based on token format
    const isFacebookToken = accessToken.startsWith('EAAc');

    // Request fields for media
    const fields = 'id,caption,media_type,media_url,permalink,timestamp,thumbnail_url';

    let url;
    if (isFacebookToken) {
      // Facebook Graph API for Business/Creator accounts
      url = `${FACEBOOK_API_BASE}/${userId}/media?fields=${fields}&limit=${limit}&access_token=${accessToken}`;
    } else {
      // Instagram Basic Display API
      url = `${INSTAGRAM_API_BASE}/${userId}/media?fields=${fields}&limit=${limit}&access_token=${accessToken}`;
    }

    console.log('Fetching Instagram posts from:', isFacebookToken ? 'Facebook Graph API' : 'Instagram Graph API');

    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Instagram API error response:', errorData);
      throw new Error(`Instagram API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Transform API response to match our app's data structure
    return data.data.map(post => ({
      id: post.id,
      imageUrl: post.media_type === 'VIDEO' ? post.thumbnail_url : post.media_url,
      caption: post.caption || '',
      date: post.timestamp,
      link: post.permalink,
      mediaType: post.media_type
    }));

  } catch (error) {
    console.error('Error fetching Instagram posts:', error);
    // Fallback to mock data on error
    return getMockInstagramPosts();
  }
};

/**
 * Mock Instagram posts for development/fallback
 * @returns {Array} Array of mock Instagram post objects
 */
const getMockInstagramPosts = () => {
  return [
    {
      id: '1',
      imageUrl: '/images/temp.png',
      caption: 'Behind the scenes of our latest shoot',
      date: '2024-01-10T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '2',
      imageUrl: '/images/temp.png',
      caption: 'Capturing the perfect moment',
      date: '2024-01-08T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '3',
      imageUrl: '/images/temp.png',
      caption: 'Cinematic storytelling',
      date: '2024-01-05T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '4',
      imageUrl: '/images/temp.png',
      caption: 'Film production excellence',
      date: '2024-01-03T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '5',
      imageUrl: '/images/temp.png',
      caption: 'Visual narrative',
      date: '2024-01-01T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '6',
      imageUrl: '/images/temp.png',
      caption: 'Creative direction',
      date: '2023-12-28T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '7',
      imageUrl: '/images/temp.png',
      caption: 'Location scouting',
      date: '2023-12-25T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '8',
      imageUrl: '/images/temp.png',
      caption: 'Post-production magic',
      date: '2023-12-20T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '9',
      imageUrl: '/images/temp.png',
      caption: 'Film aesthetics',
      date: '2023-12-15T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '10',
      imageUrl: '/images/temp.png',
      caption: 'Director vision',
      date: '2023-12-10T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '11',
      imageUrl: '/images/temp.png',
      caption: 'Crew collaboration',
      date: '2023-12-05T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    },
    {
      id: '12',
      imageUrl: '/images/temp.png',
      caption: 'Final cut preview',
      date: '2023-12-01T12:00:00.000Z',
      link: 'https://www.instagram.com/joshuagav_/',
      mediaType: 'IMAGE'
    }
  ];
};

/**
 * Refresh Instagram access token (long-lived tokens expire after 60 days)
 * @param {string} accessToken - Current access token
 * @returns {Promise<Object>} New access token and expiry
 */
export const refreshInstagramToken = async (accessToken) => {
  try {
    const url = `${INSTAGRAM_API_BASE}/refresh_access_token?grant_type=ig_refresh_token&access_token=${accessToken}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Token refresh error: ${response.status}`);
    }

    const data = await response.json();
    return {
      accessToken: data.access_token,
      expiresIn: data.expires_in
    };

  } catch (error) {
    console.error('Error refreshing Instagram token:', error);
    throw error;
  }
};
